export type TaskIntent = 'greeting' | 'question' | 'small-edit' | 'normal-work' | 'high-risk' | 'other'
export type TeamRequestMode = 'conversation' | 'project-task'

const GREETING = /^(?:merhaba|selam|selamlar|hey|hi|hello|günaydın|iyi akşamlar|iyi geceler)[\s!.,?]*$/iu
const HIGH_RISK = /\b(?:security|auth(?:entication|orization)?|payment|production|migration|architecture|data loss|güvenlik|kimlik doğrulama|ödeme|üretim|mimari|veri kaybı)\b/iu
const SMALL_EDIT = /\b(?:rename|format|translate|summari[sz]e|adını değiştir|biçimlendir|çevir|özetle)\b/iu
const NORMAL_WORK = /\b(?:implement|debug|test|refactor|feature|build|fix|uygula|hata(?:yı)? çöz|yeniden düzenle|özellik|oluştur|çalıştır)\b/iu
const PROJECT_CONTEXT = /\b(?:project|repo|repository|code|file|folder|directory|source|bug|error|proje|kod|dosya|klasör|hata|terminal|test)\b/iu
const TEAM_PROJECT_ACTION = /\b(?:build|implement|create|develop|install|configure|fix|debug|test|refactor|review|change|update|add|remove|write|run|deploy|plan|uygula|oluştur|geliştir|kur|düzelt|çöz|test et|yeniden düzenle|incele|değiştir|güncelle|ekle|kaldır|sil|yaz|çalıştır|dağıt|planla|hazırla|yap(?:ar mısın|abilir misin|ın|alım)?)\b/iu

export function classifyTaskIntent(value: string): TaskIntent {
  const text = value.trim()
  if (!text) return 'other'
  if (GREETING.test(text)) return 'greeting'
  if (HIGH_RISK.test(text)) return 'high-risk'
  if (SMALL_EDIT.test(text) && text.length <= 180) return 'small-edit'
  if (NORMAL_WORK.test(text)) return 'normal-work'
  if (/[?؟]/u.test(text) || /^(?:why|how|what|when|where|neden|nasıl|ne zaman|nerede)\b/iu.test(text)) return 'question'
  return 'other'
}

/**
 * Team Mode is still a conversation surface. Only an explicit request to do
 * project work starts the inspect -> plan -> approval workflow; greetings,
 * explanations and questions stay in the regular live Codex turn.
 */
export function classifyTeamRequest(value: string): TeamRequestMode {
  const text = value.trim()
  if (!text || GREETING.test(text)) return 'conversation'
  if (TEAM_PROJECT_ACTION.test(text)) return 'project-task'
  return 'conversation'
}

/** Memory is project context, not a default chat transcript. Keep greetings and generic questions cheap. */
export function shouldRetrieveMemory(value: string, intent = classifyTaskIntent(value)): boolean {
  const text = value.trim()
  if (!text || intent === 'greeting') return false
  if (intent === 'question') return PROJECT_CONTEXT.test(text) && text.length >= 24
  if (intent === 'other') return text.length >= 24
  return true
}

export const MEMORY_CONTEXT_TOKEN_BUDGET = 1800

export function boundMemoryContext(context: string, approximateTokens = 0) {
  const maxCharacters = MEMORY_CONTEXT_TOKEN_BUDGET * 4
  const bounded = context.slice(0, maxCharacters)
  return {
    context: bounded,
    approximateTokens: Math.min(MEMORY_CONTEXT_TOKEN_BUDGET, Math.max(0, approximateTokens || Math.ceil(bounded.length / 4))),
    truncated: bounded.length < context.length,
  }
}
